export * from "./api.js";
export * from "./audit.js";
export * from "./hours.js";
export * from "./me.js";
export * from "./permissions.js";
export * from "./preferences.js";
export * from "./support.js";
export * from "./training.js";
export * from "./users.js";
/**
 * Types and constants every package shares. Nothing here names a product
 * or a tenant (ADR-0008): the seeded tenants live in infrastructure/db/seed.
 */

export type EnvName = "dev" | "staging" | "prod";
export const ENV_NAMES: readonly EnvName[] = ["dev", "staging", "prod"];

/**
 * The reserved platform tenant (ADR-0015). Platform-scope rows —
 * occupation reference data, jurisdiction packs, platform content — carry
 * this tenant_id, and every tenant may read the published ones through
 * the one policy clause ADR-0015 §1 adds. Its id is fixed so that code,
 * migrations and seeds all mean the same row; the slug is not a DNS
 * label on purpose (ADR-0013 §1 refuses it as a tenant slug).
 */
export const PLATFORM_TENANT_ID = "00000000-0000-4000-8000-000000000001";
export const PLATFORM_TENANT_SLUG = "__platform";

/**
 * Slugs a tenant can never have (ADR-0013 §1, plus `play` from ADR-0021):
 * each is a hostname the platform itself answers on, or would confuse a
 * reader. Refused at the API and by the CHECK constraint in migration 001.
 */
export const RESERVED_TENANT_SLUGS: readonly string[] = [
  "auth",
  "docs",
  "mail",
  "www",
  "api",
  "admin",
  "app",
  "static",
  "assets",
  "status",
  "operator",
  "platform",
  "learn",
  "dev",
  "test",
  "staging",
  "prod",
  "play",
];

/**
 * SEC-05 / PLT-01: the password policy's one number and its wording, shared by the user pool (infrastructure
 * auth.ts), the invitation the chain sends (shared/email) and the temporary password the people context makes.
 */
export const PASSWORD_MIN_LENGTH = 14;
export const PASSWORD_RULES = `at least ${PASSWORD_MIN_LENGTH} characters, with an upper-case letter, a lower-case letter, a number and a symbol`;
/** Days a temporary password stays valid (the pool's `tempPasswordValidity`). */
export const TEMPORARY_PASSWORD_DAYS = 3;

/** One DNS label: 3–30 chars of [a-z0-9-], not starting or ending with a hyphen (TEN-03). */
export const TENANT_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,28})[a-z0-9]$/;

export type SlugVerdict = { ok: true } | { ok: false; reason: "format" | "reserved" };

export function checkTenantSlug(slug: string): SlugVerdict {
  if (!TENANT_SLUG_PATTERN.test(slug)) return { ok: false, reason: "format" };
  if (RESERVED_TENANT_SLUGS.includes(slug)) return { ok: false, reason: "reserved" };
  return { ok: true };
}
